import type ConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/store/schema/ConversationReadingPaneViewState';
import { getStore } from 'owa-mail-reading-pane-store/lib/store/Store';
import { mutatorAction } from 'satcheljs';
import { secondaryTabsHaveId } from 'owa-tab-store';
import getPrimaryTabId from 'owa-mail-reading-pane-store/lib/utils/getPrimaryTabId';
import type FocusedItemPart from 'owa-mail-reading-pane-store/lib/store/schema/FocusedItemPart';

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const addLoadedConversationReadingPaneViewState: (
    conversationReadingPaneViewState: ConversationReadingPaneViewState
) => void = mutatorAction(
    'addLoadedConversationReadingPaneViewState',
    function (conversationReadingPaneViewState: ConversationReadingPaneViewState): void {
        const conversationId = conversationReadingPaneViewState.conversationId.Id;
        const { loadedConversationReadingPaneViewStates } = getStore();
        if (!loadedConversationReadingPaneViewStates.has(conversationId)) {
            loadedConversationReadingPaneViewStates.set(
                conversationId,
                conversationReadingPaneViewState
            );
        }
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const releaseOrphanedLoadedConversationViewStates: () => void = mutatorAction(
    'releaseOrphanedLoadedConversationViewStates',
    function (): void {
        const { loadedConversationReadingPaneViewStates, itemPrintPaneViewStates } = getStore();
        // Release any view states that do not belong to any tabs
        loadedConversationReadingPaneViewStates.forEach((_viewState, loadedConversationId) => {
            if (
                !secondaryTabsHaveId(loadedConversationId) &&
                loadedConversationId != getPrimaryTabId()
            ) {
                loadedConversationReadingPaneViewStates.delete(loadedConversationId);
            }
        });

        // Also release any unused view states from item print pane
        itemPrintPaneViewStates &&
            itemPrintPaneViewStates.forEach((_viewState, tabId) => {
                const itemId = _viewState?.itemId;
                if (itemId && !(getPrimaryTabId() == itemId || secondaryTabsHaveId(itemId))) {
                    itemPrintPaneViewStates.delete(tabId);
                }
            });
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const releaseLoadedConversationViewState: (conversationId: string) => void = mutatorAction(
    'releaseLoadedConversationViewState',
    function (conversationId: string): void {
        getStore().loadedConversationReadingPaneViewStates.delete(conversationId);
    }
);

export const updateLoadingStateMutator: (
    conversationViewState: ConversationReadingPaneViewState,
    isLoading: boolean,
    hasLoadFailed: boolean
) => void = mutatorAction(
    'updateLoadingStateMutator',
    (
        conversationViewState: ConversationReadingPaneViewState,
        isLoading: boolean,
        hasLoadFailed: boolean
    ): void => {
        conversationViewState.loadingState.isLoading = isLoading;
        conversationViewState.loadingState.hasLoadFailed = hasLoadFailed;
    }
);

export const updateUnsupportedItemIdMutator: (
    conversationViewState: ConversationReadingPaneViewState,
    unsupportedItemId: string
) => void = mutatorAction(
    'updateUnsupportedItemIdMutator',
    (conversationViewState: ConversationReadingPaneViewState, unsupportedItemId: string): void => {
        conversationViewState.unsupportedItemId = unsupportedItemId;
    }
);

export const updateCurrentSelectedItemPartMutator: (
    conversationViewState: ConversationReadingPaneViewState,
    initiallySelectedItemPart: FocusedItemPart,
    focusedItemPart: FocusedItemPart
) => void = mutatorAction(
    'updateCurrentSelectedItemPartMutator',
    (
        conversationViewState: ConversationReadingPaneViewState,
        initiallySelectedItemPart: FocusedItemPart,
        focusedItemPart: FocusedItemPart
    ): void => {
        conversationViewState.initiallySelectedItemPart = initiallySelectedItemPart;
        conversationViewState.focusedItemPart = focusedItemPart;
    }
);

export const updateCopilotInboxScoreMutator: (
    conversationViewState: ConversationReadingPaneViewState,
    copilotInboxScore: number
) => void = mutatorAction(
    'updateCopilotInboxScoreMutator',
    (conversationViewState: ConversationReadingPaneViewState, copilotInboxScore: number): void => {
        conversationViewState.conversationCopilotInboxScore = copilotInboxScore;
    }
);
